import { useEffect, useMemo, useSyncExternalStore } from 'react'
import type { Repo } from './repos'
import { useRepos } from './queries'
import { subscribePinned, getPinned } from './pinned'
import { subscribeCarousel, getStep, getManualEpoch, autoAdvanceCarousel, slotOffset } from './carousel'

// 4 celdas (grid 2×2). Rotación ESCALONADA: cada STAGGER_MS avanza UNA sola
// celda (round-robin vía carousel.slotOffset), nunca las 4 a la vez.
const TOTAL_SLOTS = 4
const STAGGER_MS = 5_000

export interface Slot {
  repo: Repo
  mode: 'pinned' | 'carousel'
  // Índice de celda de carrusel (para rotateSlot desde los botones ◂ ▸).
  slotIndex?: number
  // Total de candidatos rotables; alimenta el badge "rota · N" del header.
  carouselTotal?: number
}

// Override del intervalo de escalonamiento para tests E2E.
function getStaggerMs(): number {
  try {
    const raw = localStorage.getItem('wiphub_carousel_rotation_ms')
    if (raw) {
      const n = Number(raw)
      if (Number.isFinite(n) && n > 0) return n
    }
  } catch {
    // ignore
  }
  return STAGGER_MS
}

// Calcula qué repo se muestra en cada una de las (hasta 4) celdas.
// - Los primeros slots están ocupados por los pinneados (en orden, sin rotar).
// - Los slots restantes rotan de forma escalonada entre los repos no pinneados.
// - Cada celda arranca en un offset distinto (stride) para no mostrar el mismo
//   repo en simultáneo.
export function useSlots(): Slot[] {
  const repos = useRepos()
  const pinnedSlugs = useSyncExternalStore(subscribePinned, getPinned, () => [] as string[])
  // step (contador global de auto-rotación) y manualEpoch viven en carousel.ts
  // para que los botones ◂ ▸ (en ProjectSection) puedan controlarlos.
  const step = useSyncExternalStore(subscribeCarousel, getStep, () => 0)
  const manualEpoch = useSyncExternalStore(subscribeCarousel, getManualEpoch, () => 0)

  // Repos no pinneados, en el orden dinámico de useRepos (push más reciente).
  const carouselPool = useMemo(
    () => repos.filter((r) => !pinnedSlugs.includes(r.slug)),
    [repos, pinnedSlugs],
  )

  const pinnedCount = pinnedSlugs.filter((s) => repos.some((r) => r.slug === s)).length
  const carouselSlotsCount = Math.max(0, Math.min(TOTAL_SLOTS - pinnedCount, carouselPool.length))

  // manualEpoch en las deps es LOAD-BEARING: cada rotación manual lo incrementa
  // → re-corre este effect → clearInterval + nuevo timer, así el carrusel no
  // auto-rota justo después de un click manual. NO quitar de las deps.
  useEffect(() => {
    if (carouselSlotsCount === 0 || carouselPool.length <= carouselSlotsCount) return
    const ms = getStaggerMs()
    const t = setInterval(() => autoAdvanceCarousel(), ms)
    return () => clearInterval(t)
  }, [carouselSlotsCount, carouselPool.length, manualEpoch])

  return useMemo<Slot[]>(() => {
    const result: Slot[] = []

    // Slots pinned primero (no rotan).
    for (const slug of pinnedSlugs) {
      const repo = repos.find((r) => r.slug === slug)
      if (repo) result.push({ repo, mode: 'pinned' })
    }

    // Slots carrusel (escalonados). Cada celda i rota SOLO dentro de su "lane":
    // los índices del pool con j ≡ i (mod carouselSlotsCount). Lanes disjuntos →
    // dos celdas NUNCA muestran el mismo repo, ni siquiera tras rotación manual.
    const len = carouselPool.length
    for (let i = 0; i < carouselSlotsCount; i++) {
      const lane: number[] = []
      for (let j = i; j < len; j += carouselSlotsCount) lane.push(j)
      if (lane.length === 0) continue
      const off = slotOffset(step, i, carouselSlotsCount)
      const laneIdx = ((off % lane.length) + lane.length) % lane.length
      result.push({ repo: carouselPool[lane[laneIdx]], mode: 'carousel', slotIndex: i, carouselTotal: len })
    }

    return result
    // manualEpoch en las deps es LOAD-BEARING: slotOffset() lee el ajuste manual
    // (carousel.manual[]) que cambia SIN tocar `step`. Sin manualEpoch el memo
    // quedaría stale tras un click en ◂ ▸.
  }, [repos, pinnedSlugs, carouselPool, carouselSlotsCount, step, manualEpoch])
}

export const TOTAL_DASHBOARD_SLOTS = TOTAL_SLOTS
